/**
 * CelebrationOverlay Component
 * 
 * Provides celebration animations for achievements, level-ups, and wins.
 * Uses confetti effects and animated notifications.
 */

import React, { useEffect, useState, useCallback } from 'react';

// Confetti particle component
const ConfettiParticle = ({ style }) => (
  <div 
    className="absolute w-3 h-3 animate-confetti"
    style={style}
  />
);

// Generate confetti particles
const generateConfetti = (count = 50) => {
  const colors = [
    '#FFD700', // Gold
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#96CEB4', // Green
    '#FFEAA7', // Yellow
    '#DDA0DD', // Plum
    '#98D8C8'  // Mint
  ];
  
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    backgroundColor: colors[Math.floor(Math.random() * colors.length)],
    animationDelay: `${Math.random() * 0.5}s`,
    animationDuration: `${2 + Math.random() * 2}s`,
    transform: `rotate(${Math.random() * 360}deg)`,
    borderRadius: Math.random() > 0.5 ? '50%' : '0'
  }));
};

/**
 * CelebrationOverlay - Shows confetti and celebration messages
 */
export const CelebrationOverlay = ({ 
  show, 
  type = 'points', 
  intensity = 'medium',
  title,
  subtitle,
  icon,
  onComplete 
}) => {
  const [confetti, setConfetti] = useState([]);
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    if (show) {
      setVisible(true);
      const count = intensity === 'high' ? 80 : intensity === 'medium' ? 50 : 25;
      setConfetti(generateConfetti(count));
      
      // Auto-hide after animation
      const timer = setTimeout(() => {
        setVisible(false);
        if (onComplete) onComplete();
      }, 3500);
      
      return () => clearTimeout(timer);
    }
  }, [show, intensity, onComplete]);
  
  if (!visible) return null;
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {/* Confetti */}
      {confetti.map(particle => (
        <ConfettiParticle 
          key={particle.id} 
          style={{
            left: particle.left,
            backgroundColor: particle.backgroundColor,
            animationDelay: particle.animationDelay,
            animationDuration: particle.animationDuration,
            transform: particle.transform,
            borderRadius: particle.borderRadius
          }}
        />
      ))}
      
      {/* Center celebration card */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`
          transform transition-all duration-500 ease-out
          ${visible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}
          bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4
          border-4 ${type === 'level_up' ? 'border-yellow-400' : type === 'achievement' ? 'border-purple-400' : 'border-green-400'}
          animate-bounce-once
        `}>
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse-scale">
              {icon || (type === 'level_up' ? '🎊' : type === 'achievement' ? '🏆' : '🎉')}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {title || 'Congratulations!'}
            </h2>
            {subtitle && (
              <p className="text-gray-600">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * PointsPopup - Small popup showing points earned
 */
export const PointsPopup = ({ points, show, position = 'top-right' }) => {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    if (show && points > 0) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [show, points]);
  
  if (!visible) return null;
  
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4'
  };
  
  return (
    <div className={`fixed ${positionClasses[position]} z-50 animate-slide-up-fade`}>
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
        <span className="text-lg">⚡</span>
        <span className="font-bold">+{points} points</span>
      </div>
    </div>
  );
};

/**
 * AchievementUnlockedModal - Full achievement unlock celebration
 */
export const AchievementUnlockedModal = ({ achievement, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);
  
  if (!show || !achievement) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform animate-scale-up">
        <div className="text-center">
          <div className="text-sm font-semibold text-purple-600 uppercase tracking-wide mb-2">
            Achievement Unlocked!
          </div>
          <div className="text-8xl mb-4 animate-bounce">
            {achievement.icon}
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {achievement.name}
          </h2>
          <p className="text-gray-600 mb-4">
            {achievement.description}
          </p>
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full">
            <span>🌟</span>
            <span className="font-bold">+{achievement.points} bonus points</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="mt-6 w-full py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
};

/**
 * LevelUpModal - Level up celebration
 */
export const LevelUpModal = ({ oldLevel, newLevel, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);
  
  if (!show || !newLevel) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in">
      <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-2xl p-1 max-w-md mx-4 transform animate-scale-up">
        <div className="bg-white rounded-xl p-8">
          <div className="text-center">
            <div className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-2">
              Level Up!
            </div>
            <div className="flex items-center justify-center gap-4 mb-4">
              {oldLevel && (
                <>
                  <div className="text-4xl opacity-50">{oldLevel.icon}</div>
                  <div className="text-2xl text-gray-400">→</div>
                </>
              )}
              <div className="text-7xl animate-pulse-scale">{newLevel.icon}</div>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Level {newLevel.level}
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              {newLevel.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="mt-6 w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * StreakNotification - Daily streak notification
 */
export const StreakNotification = ({ streak, bonus, show, onDismiss }) => {
  if (!show) return null;
  
  const isWeekMilestone = streak === 7;
  const isMonthMilestone = streak === 30;
  const isMilestone = isWeekMilestone || isMonthMilestone;
  
  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div className={`
        rounded-lg shadow-lg p-4 max-w-sm
        ${isMilestone 
          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
          : 'bg-white border border-gray-200'
        }
      `}>
        <div className="flex items-center gap-3">
          <div className={`text-3xl ${isMilestone ? 'animate-bounce' : ''}`}>
            🔥
          </div>
          <div>
            <div className={`font-bold ${isMilestone ? 'text-white' : 'text-gray-800'}`}>
              {isMilestone ? `${streak}-Day Streak!` : `${streak} Day Streak`}
            </div>
            <div className={`text-sm ${isMilestone ? 'text-orange-100' : 'text-gray-600'}`}>
              +{bonus} points earned
            </div>
          </div>
          <button
            onClick={onDismiss}
            className={`ml-auto p-1 rounded-full hover:bg-black/10 ${isMilestone ? 'text-white' : 'text-gray-400'}`}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default CelebrationOverlay;
